/*
 * <review-carousel> — smooth scroll-snap testimonial slider.
 * Dots + arrows navigate; the active dot tracks the scroll position.
 * Optional gentle auto-advance that pauses on hover / focus / touch / tab-hidden.
 */
if (!customElements.get('review-carousel')) {
  customElements.define(
    'review-carousel',
    class ReviewCarousel extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('.rcar__track');
        this.slides = Array.from(this.querySelectorAll('.rcar__slide'));
        this.dots = Array.from(this.querySelectorAll('[data-rcar-dot]'));
        this.autoplay = this.dataset.autoplay === 'true';
        this.speed = parseInt(this.dataset.speed, 10) || 5000;
        this.index = 0;
      }

      connectedCallback() {
        if (!this.track || this.slides.length === 0) return;

        this.prevBtn = this.querySelector('[data-rcar-prev]');
        this.nextBtn = this.querySelector('[data-rcar-next]');
        this.prevBtn?.addEventListener('click', () => this.go(this.index - 1));
        this.nextBtn?.addEventListener('click', () => this.go(this.index + 1));
        this.dots.forEach((dot) =>
          dot.addEventListener('click', () => this.go(parseInt(dot.dataset.rcarDot, 10)))
        );

        // Keep the active dot in sync with manual scrolling / swiping.
        let raf;
        this.track.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => this.syncActive());
          },
          { passive: true }
        );

        // Hide arrows when the track doesn't overflow; recompute on resize/load.
        this._updateNav = this.updateNav.bind(this);
        window.addEventListener('resize', this._updateNav);
        window.addEventListener('load', this._updateNav);
        if ('ResizeObserver' in window) {
          this._ro = new ResizeObserver(() => this.updateNav());
          this._ro.observe(this.track);
        }
        this.updateNav();
        setTimeout(this._updateNav, 300);

        // Autoplay + pause on interaction.
        if (this.autoplay) {
          this.start();
          ['mouseenter', 'focusin', 'touchstart'].forEach((ev) =>
            this.addEventListener(ev, () => this.stop(), { passive: true })
          );
          ['mouseleave', 'focusout'].forEach((ev) =>
            this.addEventListener(ev, () => this.start())
          );
          document.addEventListener('visibilitychange', () =>
            document.hidden ? this.stop() : this.start()
          );
        }

        this.syncActive();
      }

      disconnectedCallback() {
        this.stop();
      }

      /* Scroll to slide i (wraps), smoothly. Uses rect math so it's correct
         regardless of offset parents / padding. */
      go(i) {
        const count = this.slides.length;
        this.index = ((i % count) + count) % count;
        const slide = this.slides[this.index];
        const left =
          slide.getBoundingClientRect().left -
          this.track.getBoundingClientRect().left +
          this.track.scrollLeft;
        this.track.scrollTo({ left, behavior: 'smooth' });
        this.setActive(this.index);
      }

      /* Hide arrows entirely when there's nothing to scroll. */
      updateNav() {
        if (!this.track) return;
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 2;
        [this.prevBtn, this.nextBtn].forEach((b) => {
          if (b) b.hidden = !scrollable;
        });
      }

      /* Work out which slide is nearest the left edge and mark its dot. */
      syncActive() {
        const left = this.track.scrollLeft;
        let nearest = 0;
        let min = Infinity;
        this.slides.forEach((s, i) => {
          const d = Math.abs(s.offsetLeft - this.track.offsetLeft - left);
          if (d < min) {
            min = d;
            nearest = i;
          }
        });
        this.index = nearest;
        this.setActive(nearest);
      }

      setActive(i) {
        this.dots.forEach((d, di) => {
          d.classList.toggle('is-active', di === i);
          d.setAttribute('aria-selected', di === i ? 'true' : 'false');
        });
      }

      start() {
        if (!this.autoplay || this.timer) return;
        this.timer = setInterval(() => this.go(this.index + 1), this.speed);
      }

      stop() {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  );
}
